import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useAuth } from '../../contexts/AuthContext';
import { gymService } from '../../lib/services';

import RoleBasedHeader from '../../components/ui/RoleBasedHeader';
import DashboardNavigation from '../../components/ui/DashboardNavigation';
import QuickActionPanel from '../../components/ui/QuickActionPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

import KPICard from './components/KPICard';
import RevenueChart from './components/RevenueChart';
import MemberAnalytics from './components/MemberAnalytics';
import FinancialSummary from './components/FinancialSummary';
import OperationalOverview from './components/OperationalOverview';
import GoalTracker from './components/GoalTracker';

const SingleGymOwnerDashboard = () => {
  const { userProfile } = useAuth();
  const [gymData, setGymData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');

  useEffect(() => {
    loadGymData();
  }, [userProfile?.gym_id]);

  const loadGymData = async () => {
    if (!userProfile?.gym_id) {
      setLoading(false);
      return;
    }

    try {
      const { data, error } = await gymService?.getGymById(userProfile?.gym_id);
      if (!error && data) {
        setGymData(data);
      }
    } catch (error) {
      console.error('Error loading gym data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Mock KPI data - in production, this would come from analytics service
  const kpiData = [
    {
      title: 'Daily Revenue',
      value: '$3,450',
      change: '+12.5%',
      changeType: 'positive',
      icon: 'DollarSign',
      iconColor: 'var(--color-success)',
      trend: 'up',
      subtitle: 'vs yesterday'
    },
    {
      title: 'Active Members',
      value: '847',
      change: '+8.2%',
      changeType: 'positive',
      icon: 'Users',
      iconColor: 'var(--color-primary)',
      trend: 'up',
      subtitle: 'this month'
    },
    {
      title: 'Class Utilization',
      value: '82%',
      change: '+5.3%',
      changeType: 'positive',
      icon: 'Activity',
      iconColor: 'var(--color-accent)',
      trend: 'up',
      subtitle: 'capacity filled'
    },
    {
      title: 'Staff Productivity',
      value: '94%',
      change: '+2.1%',
      changeType: 'positive',
      icon: 'TrendingUp',
      iconColor: 'var(--color-info)',
      trend: 'up',
      subtitle: 'efficiency score'
    }
  ];

  const revenueData = [
    { month: 'Jan', revenue: 85000, expenses: 52000, profit: 33000 },
    { month: 'Feb', revenue: 88000, expenses: 54000, profit: 34000 },
    { month: 'Mar', revenue: 92000, expenses: 55000, profit: 37000 },
    { month: 'Apr', revenue: 95000, expenses: 56000, profit: 39000 },
    { month: 'May', revenue: 98000, expenses: 57000, profit: 41000 },
    { month: 'Jun', revenue: 102000, expenses: 58000, profit: 44000 }
  ];

  const memberAnalyticsData = {
    totalMembers: 847,
    newThisMonth: 67,
    churnRate: 3.2,
    retentionRate: 96.8,
    demographics: [
      { ageGroup: '18-25', count: 152, percentage: 18 },
      { ageGroup: '26-35', count: 305, percentage: 36 },
      { ageGroup: '36-45', count: 237, percentage: 28 },
      { ageGroup: '46-55', count: 118, percentage: 14 },
      { ageGroup: '56+', count: 35, percentage: 4 }
    ],
    membershipTypes: [
      { type: 'Premium', count: 254, revenue: 38100 },
      { type: 'Standard', count: 423, revenue: 33840 },
      { type: 'Basic', count: 170, revenue: 8500 }
    ]
  };

  const financialData = {
    monthlyRevenue: 102000,
    monthlyExpenses: 58000,
    profitMargin: 43.1,
    cashFlow: 44000,
    expenseBreakdown: [
      { category: 'Staff Salaries', amount: 32000, percentage: 55 },
      { category: 'Rent & Utilities', amount: 15000, percentage: 26 },
      { category: 'Equipment', amount: 6000, percentage: 10 },
      { category: 'Marketing', amount: 3000, percentage: 5 },
      { category: 'Other', amount: 2000, percentage: 4 }
    ],
    alerts: [
      { type: 'warning', message: 'Equipment maintenance budget 85% utilized' },
      { type: 'info', message: '12 pending payment follow-ups' }
    ]
  };

  const operationalData = {
    equipmentStatus: {
      operational: 142,
      maintenance: 8,
      outOfService: 2
    },
    staffSchedule: {
      onDuty: 12,
      scheduled: 18,
      conflicts: 2
    },
    facilityCapacity: {
      current: 247,
      maximum: 300,
      utilization: 82
    },
    upcomingMaintenance: [
      { item: 'Treadmill #5', date: '2026-01-28', priority: 'high' },
      { item: 'HVAC System', date: '2026-02-01', priority: 'medium' },
      { item: 'Pool Filtration', date: '2026-02-05', priority: 'low' }
    ]
  };

  const goalsData = [
    {
      title: 'Monthly Revenue Target',
      current: 102000,
      target: 110000,
      progress: 93,
      status: 'on-track'
    },
    {
      title: 'New Member Acquisition',
      current: 67,
      target: 75,
      progress: 89,
      status: 'on-track'
    },
    {
      title: 'Member Retention Rate',
      current: 96.8,
      target: 95,
      progress: 100,
      status: 'achieved'
    },
    {
      title: 'Class Utilization',
      current: 82,
      target: 85,
      progress: 96,
      status: 'on-track'
    }
  ];

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <DashboardNavigation />
        </div>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="Loader" className="w-8 h-8 animate-spin mx-auto mb-4 text-primary-600" />
            <p className="text-gray-600">Loading gym data...</p>
          </div>
        </div>
      </div>
    );
  }

  if (!gymData) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <DashboardNavigation />
        </div>
        <div className="flex items-center justify-center h-96">
          <div className="text-center">
            <Icon name="AlertCircle" className="w-12 h-12 mx-auto mb-4 text-yellow-500" />
            <h2 className="text-xl font-semibold text-gray-800 mb-2">No Gym Assigned</h2>
            <p className="text-gray-600">Please contact your administrator to assign a gym location.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Single Gym Dashboard - {gymData?.name || 'PulseLoom'}</title>
        <meta name="description" content="Comprehensive gym management dashboard for single location owners" />
      </Helmet>

      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="page-content-wrapper">
          <DashboardNavigation />
        </div>
        <QuickActionPanel onAction={(action) => console.log('Quick action:', action)} />

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          {/* Gym Header */}
          <div className="mb-8">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{gymData?.name}</h1>
                <p className="text-gray-600 flex items-center gap-2">
                  <Icon name="MapPin" className="w-4 h-4" />
                  {gymData?.address}, {gymData?.city}, {gymData?.state} {gymData?.zip_code}
                </p>
              </div>
              <div className="flex items-center gap-3">
                <select
                  value={dateRange}
                  onChange={(e) => setDateRange(e?.target?.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent"
                >
                  <option value="week">Last 7 Days</option>
                  <option value="month">Last 30 Days</option>
                  <option value="quarter">Last Quarter</option>
                  <option value="year">Last Year</option>
                </select>
                <Button variant="primary" className="flex items-center gap-2">
                  <Icon name="Download" className="w-4 h-4" />
                  Export Report
                </Button>
              </div>
            </div>
          </div>

          {/* KPI Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            {kpiData?.map((kpi, index) => (
              <KPICard key={index} {...kpi} />
            ))}
          </div>

          {/* Revenue Chart */}
          <div className="mb-8">
            <RevenueChart data={revenueData} dateRange={dateRange} />
          </div>

          {/* Two Column Layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Member Analytics */}
            <MemberAnalytics data={memberAnalyticsData} />
            
            {/* Financial Summary */}
            <FinancialSummary data={financialData} />
          </div>

          {/* Operational Overview */}
          <div className="mb-8">
            <OperationalOverview data={operationalData} />
          </div>

          {/* Goal Tracker */}
          <div className="mb-8">
            <GoalTracker goals={goalsData} />
          </div>

          {/* Quick Actions */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Quick Actions</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Button variant="outline" className="flex items-center justify-center gap-2 py-3">
                <Icon name="Users" className="w-5 h-5" />
                Manage Members
              </Button>
              <Button variant="outline" className="flex items-center justify-center gap-2 py-3">
                <Icon name="Calendar" className="w-5 h-5" />
                Staff Scheduling
              </Button>
              <Button variant="outline" className="flex items-center justify-center gap-2 py-3">
                <Icon name="FileText" className="w-5 h-5" />
                Financial Reports
              </Button>
            </div>
          </div>
        </main>
      </div>
    </>
  );
};

export default SingleGymOwnerDashboard;